import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useApi, fmtDate } from "@/lib/api";
import { parseList } from "@/lib/sp";

const SKIP = new Set(["id", "password_hash", "storage_path", "is_deleted", "patient_id", "doctor_id", "owner_id", "created_by"]);
const ISO = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2})?/;
const LABELS = { patient_name: "Enfant", doctor_name: "Médecin", owner_name: "Ajouté par", created_at: "Créé le", updated_at: "Mis à jour", name: "Nom", status: "Statut" };

export const fmtValue = (v) => {
  if (v == null || v === "") return "—";
  if (typeof v === "boolean") return v ? "Oui" : "Non";
  if (typeof v === "string" && ISO.test(v)) return fmtDate(v.slice(0, v.includes("T") ? 16 : 10));
  const list = Array.isArray(v) ? v : typeof v === "string" && v.startsWith("[") ? parseList(v) : null;
  if (list) return list.map((x) => (typeof x === "object" ? x.name || JSON.stringify(x) : x)).join(", ") || "—";
  if (typeof v === "object") return JSON.stringify(v).slice(0, 120);
  return String(v);
};

export const genericColumns = (items) => {
  const keys = [];
  items.forEach((it) => Object.keys(it).forEach((k) => { if (!SKIP.has(k) && !k.endsWith("_id") && !keys.includes(k)) keys.push(k); }));
  const first = ["patient_name", "doctor_name"].filter((k) => keys.includes(k));
  return [...first, ...keys.filter((k) => !first.includes(k))].slice(0, 8);
};

export default function GenericTable({ collection }) {
  const api = useApi();
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");
  useEffect(() => { api("get", `/sp/${collection}`).then(setItems).catch(() => setError(true)); }, [api, collection]);
  const cols = useMemo(() => genericColumns(items || []), [items]);
  const rows = (items || []).filter((i) => !q || JSON.stringify(i).toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="bg-white border border-stone-200 rounded-lg" data-testid={`generic-${collection}`}>
      <div className="p-4 border-b border-stone-200 flex items-center gap-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="max-w-sm" data-testid={`generic-${collection}-search`} />
        <span className="ml-auto text-xs text-stone-500" data-testid={`generic-${collection}-count`}>{rows.length} élément(s)</span>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>{cols.map((c) => <TableHead key={c} className="text-xs uppercase tracking-[0.1em] text-stone-500">{LABELS[c] || c.replace(/_/g, " ")}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {items === null && <TableRow><TableCell className="py-10 text-center text-stone-500" colSpan={cols.length || 1} data-testid={`generic-${collection}-status`}>{error ? "Impossible de charger ces données. Rechargez la page." : "Chargement…"}</TableCell></TableRow>}
            {items && rows.length === 0 && <TableRow><TableCell className="py-10 text-center text-stone-500" colSpan={cols.length || 1}>Aucune donnée reçue de l'application pour l'instant.</TableCell></TableRow>}
            {rows.map((it) => (
              <TableRow key={it.id}>
                {cols.map((c) => (
                  <TableCell key={c} className="text-sm text-stone-700">
                    {c === "patient_name" && it.patient_id ? <Link to={`/enfants/${it.patient_id}`} className="text-[#C2410C] hover:underline">{it.patient_name}</Link> : fmtValue(it[c])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
